import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PhotoPicker } from "./shared/PhotoPicker";
import { PhotoThumb } from "./shared/PhotoThumb";
import { MachineSelect } from "./shared/MachineSelect";
import { FilterBar } from "./shared/FilterBar";
import { useMyAssets } from "./shared/useMyAssets";
import { NoAccess } from "./shared/NoAccess";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationBar } from "@/components/shared/PaginationBar";

const VIEW_ROLES = ["Director", "Manager", "Accounts", "Back Office"];

function SubmitForm() {
  const { assets, needsPicker } = useMyAssets();
  const [machineAssetId, setMachineAssetId] = useState("");
  const [litres, setLitres] = useState("");
  const [hmrReading, setHmrReading] = useState("");
  const [remarks, setRemarks] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (needsPicker && !machineAssetId) return toast.error("Please select which machine this entry is for.");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("stage", "After");
    fd.append("litres_filled", litres);
    fd.append("hmr_reading", hmrReading);
    if (remarks) fd.append("remarks", remarks);
    if (machineAssetId) fd.append("machine_asset_id", machineAssetId);
    photos.forEach((f) => fd.append("photos", f));
    try {
      await client.post("/fleetcare/diesel-filling", fd);
      toast.success("Diesel Filling entry submitted");
      setLitres("");
      setHmrReading("");
      setRemarks("");
      setPhotos([]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-w-lg" data-testid="diesel-filling-submit-form">
      {needsPicker && <MachineSelect assets={assets} value={machineAssetId} onChange={setMachineAssetId} testId="diesel-filling-machine-select" />}
      <PhotoPicker label="Photo" files={photos} onChange={setPhotos} testId="diesel-filling-photo-picker" />
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1.5">
          Litres Filled <span className="text-red-500">*</span>
        </p>
        <Input type="number" step="0.01" data-testid="diesel-filling-litres-input" value={litres} onChange={(e) => setLitres(e.target.value)} required />
      </div>
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1.5">
          HMR Reading <span className="text-red-500">*</span>
        </p>
        <Input data-testid="diesel-filling-hmr-input" value={hmrReading} onChange={(e) => setHmrReading(e.target.value)} required />
      </div>
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1.5">Remarks</p>
        <Textarea data-testid="diesel-filling-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>
      <Button type="submit" data-testid="diesel-filling-submit-button" disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
        {submitting ? "Submitting..." : "Submit Diesel Filling"}
      </Button>
    </form>
  );
}

function DetailPanel({ entry, onClose }) {
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="diesel-filling-detail-panel">
        <SheetHeader>
          <SheetTitle>{entry.asset_label}</SheetTitle>
          <SheetDescription>
            {entry.employee_name} · {entry.stage} Filling · {new Date(entry.created_at).toLocaleString("en-IN")}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-2 flex-wrap">
            {(entry.photos || []).map((p, i) => (
              <PhotoThumb key={i} path={`/fleetcare/diesel-filling/${entry.id}/photos/${i}`} alt={p.filename} testId={`diesel-filling-photo-${i}`} />
            ))}
          </div>
          {entry.litres_filled != null && (
            <p className="text-sm">
              <span className="text-slate-500">Litres Filled:</span> <span className="font-mono font-medium">{entry.litres_filled}</span>
            </p>
          )}
          {entry.hmr_reading && (
            <p className="text-sm">
              <span className="text-slate-500">HMR Reading:</span> <span className="font-mono font-medium">{entry.hmr_reading}</span>
            </p>
          )}
          {entry.remarks && (
            <p className="text-sm">
              <span className="text-slate-500">Remarks:</span> {entry.remarks}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ViewList() {
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "", assetId: "" });
  const params = {};
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.assetId) params.asset_id = filters.assetId;
  const { items: entries, loading, page, setPage, totalPages } = usePaginatedList("/fleetcare/diesel-filling", params);
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-4" data-testid="diesel-filling-view">
      <FilterBar {...filters} onChange={(v) => setFilters((f) => ({ ...f, ...v }))} testIdPrefix="diesel-filling" />
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="diesel-filling-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Machine</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead>Litres</TableHead>
              <TableHead>HMR Reading</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  No entries found.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} data-testid={`diesel-filling-row-${e.id}`} className="cursor-pointer" onClick={() => setSelected(e)}>
                  <TableCell>{new Date(e.created_at).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{e.stage}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.asset_label}</TableCell>
                  <TableCell>{e.employee_name}</TableCell>
                  <TableCell className="font-mono">{e.litres_filled ?? "—"}</TableCell>
                  <TableCell className="font-mono">{e.hmr_reading || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage} testId="diesel-filling-pagination" />
      {selected && <DetailPanel entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default function DieselFillingTab() {
  const { user } = useAuth();
  if (user?.role === "Operator") return <SubmitForm />;
  if (VIEW_ROLES.includes(user?.role)) return <ViewList />;
  return <NoAccess />;
}
